import React, { useEffect, useState } from 'react';
import { KitchenService } from '../../../services/KitchenService';
import { useLocalStorage } from '../../../auth/useLocalStorage';
import ProductionEstimateCard from './ProductionEstimateCard';
import { ProductEstimate } from '../../../utils/productionEstimates';
import './ProductionEstimates.css';

interface ProductionEstimatesData {
  kitchen_id?: number;
  warehouse_id?: number;
  warehouse_name?: string;
  window_minutes: number | string;
  target_time: string;
  target_time_formatted?: string;
  generated_at: string;
  estimates: ProductEstimate[];
  summary?: {
    total_products: number;
    total_cooking_time: number;
    earliest_start_time: string;
  };
}

const ProductionEstimates: React.FC = () => {
  const [user] = useLocalStorage('user', null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ProductionEstimatesData | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const fetchEstimates = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📊 [PRODUCTION ESTIMATES] Fetching production estimates...');

      const response = await KitchenService.getProductionEstimates(user.kitchen_id as string);

      console.log('📊 [PRODUCTION ESTIMATES] Response:', response);

      if (response.success && response.object) {
        setData(response.object);
        setLastUpdate(new Date());
      } else {
        setError(response.message || 'Error al cargar estimaciones');
      }
    } catch (err: any) {
      console.error('❌ [PRODUCTION ESTIMATES] Error:', err);
      setError(err.message || 'Error al cargar estimaciones. Intentar de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.kitchen_id) return;

    fetchEstimates();

    // Actualizar cada 5 minutos
    const fiveMinInterval = setInterval(() => {
      console.log('🔄 [PRODUCTION ESTIMATES] Auto-refresh (5 min) triggered');
      fetchEstimates();
    }, 5 * 60 * 1000);

    // Actualizar exactamente cuando cambia la hora
    let hourTimeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextHour = () => {
      const now = new Date();
      const msUntilNextHour =
        (60 - now.getMinutes()) * 60 * 1000
        - now.getSeconds() * 1000
        - now.getMilliseconds();

      hourTimeoutId = setTimeout(() => {
        console.log('🕐 [PRODUCTION ESTIMATES] Hour changed, refreshing estimates...');
        fetchEstimates();
        scheduleNextHour();
      }, msUntilNextHour);
    };

    scheduleNextHour();

    return () => {
      clearInterval(fiveMinInterval);
      clearTimeout(hourTimeoutId);
    };
  }, [user?.kitchen_id]);

  const formatLastUpdate = () => {
    return lastUpdate.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading && !data) {
    return (
      <div className="production-estimates-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando estimaciones de producción...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="production-estimates-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error al cargar estimaciones</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={fetchEstimates}>
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.estimates.length === 0) {
    return (
      <div className="production-estimates-container">
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No hay estimaciones disponibles</h3>
          <p>No hay estimaciones de producción para los próximos {data?.window_minutes || 60} minutos.</p>
          <p className="empty-hint">
            Las estimaciones se generan automáticamente basándose en datos históricos de ventas.
          </p>
          <button className="retry-button" onClick={fetchEstimates}>
            Actualizar
          </button>
        </div>
      </div>
    );
  }

  // Calcular valores si summary no está disponible
  const totalProducts = data.summary?.total_products ?? data.estimates.length;
  const totalCookingTime = data.summary?.total_cooking_time ??
    data.estimates.reduce((sum, est) => sum + (est.cooking_time || est.cooking_time_minutes || 0), 0);

  return (
    <div className="production-estimates-container">
      {/* Header compacto */}
      <div className="estimates-header">
        <div className="header-left">
          <span className="header-title">📊 Producción Anticipada</span>
          <span className="header-subtitle">{data.warehouse_name || `Cocina #${data.warehouse_id || user.kitchen_id}`} · {data.window_minutes} min</span>
        </div>
        <div className="header-right">
          <span className="summary-pill">🍖 <strong>{totalProducts}</strong> productos</span>
          <span className="summary-pill">⏱️ <strong>{totalCookingTime}</strong> min cocción</span>
          <span className="meta-update">🕐 {formatLastUpdate()}</span>
          <button className="refresh-button" onClick={fetchEstimates} disabled={loading}>
            {loading ? '⏳' : '🔄'}
          </button>
        </div>
      </div>

      {/* Grid de cards de estimaciones */}
      <div className="estimates-grid">
        {data.estimates.map((estimate) => (
          <ProductionEstimateCard
            key={estimate.product_id}
            estimate={estimate}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductionEstimates;
